import { SupabaseClient } from '@supabase/supabase-js';
import React, { useEffect, useRef, useState } from 'react';
import {
  RedirectTo,
  ViewSignUp,
  ViewSignIn,
  ViewType,
} from '@habity/types/auth';
import { VIEWS } from '@habity/constants';
import { Link, Button, Container, Input, Text, Alert } from '@chakra-ui/react';
import { useTranslations } from 'next-intl';
import { TODO } from '@habity/types';

export interface EmailAuthProps {
  authView?: ViewSignIn | ViewSignUp;
  defaultEmail?: string;
  defaultPassword?: string;
  setAuthView?: TODO;
  setDefaultEmail?: (email: string) => void;
  setDefaultPassword?: (password: string) => void;
  supabaseClient: SupabaseClient;
  showLinks?: boolean;
  redirectTo?: RedirectTo;
  magicLink?: boolean;
}

function EmailAuth({
  authView = 'sign_in',
  defaultEmail = '',
  defaultPassword = '',
  setAuthView = () => null,
  setDefaultEmail = () => null,
  setDefaultPassword = () => null,
  supabaseClient,
  showLinks = false,
  redirectTo,
  magicLink,
}: EmailAuthProps) {
  const isMounted = useRef<boolean>(true);
  const [email, setEmail] = useState(defaultEmail);
  const [password, setPassword] = useState(defaultPassword);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const t = useTranslations('Auth');

  useEffect(() => {
    isMounted.current = true;
    setEmail(defaultEmail);
    setPassword(defaultPassword);

    return () => {
      isMounted.current = false;
    };
  }, [authView]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    switch (authView) {
      case 'sign_in':
        const { error: signInError } =
          await supabaseClient.auth.signInWithPassword({
            email,
            password,
          });
        if (signInError) setError(signInError.message);
        break;
      case 'sign_up':
        const {
          data: { user: signUpUser, session: signUpSession },
          error: signUpError,
        } = await supabaseClient.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: redirectTo,
          },
        });
        if (signUpError) setError(signUpError.message);
        // Check if session is null -> email confirmation setting is turned on
        else if (signUpUser && !signUpSession)
          setMessage(t('sign_up.confirmation_text'));
        break;
    }

    /*
     * it is possible the auth component may have been unmounted at this point
     * check if component is mounted before setting a useState
     */
    if (isMounted.current) setLoading(false);
  };

  const handleViewChange = (newView: ViewType) => {
    setDefaultEmail(email);
    setDefaultPassword(password);
    setAuthView(newView);
  };

  const labels = useTranslations(`Auth.${authView}`);

  return (
    <form
      id={authView === 'sign_in' ? `auth-sign-in` : `auth-sign-up`}
      onSubmit={handleSubmit}
      autoComplete={'on'}
      style={{ width: '100%' }}
    >
      <Container gap="large">
        <Container gap="large">
          <div>
            <Text>{labels('email_label')}</Text>
            <Input
              id="email"
              type="email"
              name="email"
              autoFocus
              placeholder={labels('email_input_placeholder')}
              defaultValue={email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setEmail(e.target.value)
              }
              autoComplete="email"
            />
          </div>
          <div>
            <Text>{labels('password_label')}</Text>
            <Input
              id="password"
              type="password"
              name="password"
              placeholder={labels('password_input_placeholder')}
              defaultValue={password}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setPassword(e.target.value)
              }
              autoComplete={
                authView === 'sign_in' ? 'current-password' : 'new-password'
              }
            />
          </div>
        </Container>

        <Button type="submit" color="primary">
          {loading ? labels('loading_button_label') : labels('button_label')}
        </Button>

        {showLinks && (
          <Container gap="small">
            {authView === VIEWS.SIGN_IN && magicLink && (
              <Link
                href="#auth-magic-link"
                onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                  e.preventDefault();
                  setAuthView(VIEWS.MAGIC_LINK);
                }}
              >
                {t('magic_link.link_text')}
              </Link>
            )}
            {authView === VIEWS.SIGN_IN && (
              <Link
                href="#auth-forgot-password"
                onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                  e.preventDefault();
                  setAuthView(VIEWS.FORGOTTEN_PASSWORD);
                }}
              >
                {t('forgotten_password.link_text')}
              </Link>
            )}
            {authView === VIEWS.SIGN_IN ? (
              <Link
                href="#auth-sign-up"
                onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                  e.preventDefault();
                  handleViewChange(VIEWS.SIGN_UP);
                }}
              >
                {t('sign_up.link_text')}
              </Link>
            ) : (
              <Link
                href="#auth-sign-in"
                onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                  e.preventDefault();
                  handleViewChange(VIEWS.SIGN_IN);
                }}
              >
                {t('sign_in.link_text')}
              </Link>
            )}
          </Container>
        )}
      </Container>
      {message && <Alert>{message}</Alert>}
      {error && <Alert status="error">{error}</Alert>}
    </form>
  );
}

export { EmailAuth };
